import { useFormik } from 'formik';
import { FC, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Loader, Password, Text } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../assets/logo_completo.png';
import { PrivilegeContext } from '../../components/priviledge/PriviledgeProvider';
import { login, User } from '../../redux/authSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { LoginService } from '../../services/auth/loginService';

const Login: FC = () => {

  // STATES

  const [isLoading, setIsLoading] = useState(false);

  // HOOKS

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userCan } = useContext(PrivilegeContext);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // REDIRECT

  if (isAuthenticated) {
    setTimeout(() => {
        navigate('/', { replace: true })
    }, 100);
  };

  // FUNCTIONS

  // -----------------------------------------------------------------------------------------------------------------------------------------------
  /**
   * ES: FUNCIÓN PARA INICIAR SESIÓN
   * EN: FUNCTION TO LOGIN
   * 
   * @param username ES: Nombre de usuario EN: Username
   * @param password ES: Contraseña EN: Password
   * @returns
   */
  // -----------------------------------------------------------------------------------------------------------------------------------------------
  const handleLogin = async (username: string, password: string) => {
    setIsLoading(true);
    const response = await (await (new LoginService()).authUserCredentials(username, password)).getResponseData();

    if (!response?.success) {
      toast.error(response.message);
      setIsLoading(false);
      dispatch(login({
        isAuthenticated: false,
        loading: false,
        error: response.message,
        user: null
      }))
      return;
    }

    if (response && response.success) {

      try {
        const user: User = {
          id: response.data.user.id,
          email: response.data.user.email,
          token: response.data.token,
          name: response.data.user.name,
          lastName: response.data.user.lastName,
          profileImage: response.data.user.imgProfile,
          roles: response.data.user.roles,
          companyId: response.data.user?.company || '',
          companyName: response.data.user.companyName || '',
          dashboard: response.data.user.permissions[response.data.user?.company]?.dashboard !== null && response.data.user.permissions[response.data.user?.company]?.dashboard !== undefined
        };

        dispatch(login({
          isAuthenticated: true,
          loading: false,
          error: null,
          user: user
        }))

      } catch (error) {
        toast.error('Error saving user to indexDb');
        return;
      } finally {
        setIsLoading(false);
      }
    }
    
  };
  // -----------------------------------------------------------------------------------------------------------------------------------------------

  // FORMIK
  
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      loginUsername: '',
      loginPassword: '',
    },
    validate: (values) => {
      const errors: { loginUsername?: string; loginPassword?: string } = {};

      if (!values.loginUsername) {
        errors.loginUsername = 'Requerido';
      }

      if (!values.loginPassword) {
        errors.loginPassword = 'Requerido';
      }

      return errors;
    },
    validateOnChange: false,
    onSubmit: (values) => { handleLogin(values.loginUsername, values.loginPassword) },
  });

  // RENDER

  return (
    <div className='grid grid-cols-1 md:grid-cols-3 h-screen'>
      <div className='flex justify-start items-center mx-4'>
        <div className='container mx-4 px-5'>
          <Link to='/' className={'flex justify-start items-center mb-5'}>
            <img src={fullLogo} height={'30vh'} alt="Logo de Austros" />
          </Link>
          <Text className="text-4xl font-bold text-gray-800 mb-5">Inicia sesión</Text>
          <form onSubmit={formik.handleSubmit} className='mt-5'>
            <div className="space-y-5">
                <Input
                  id='loginUsername'
                  type="email"
                  size="lg"
                  label="Correo Electrónico"
                  placeholder="Introducte tu email"
                  className="[&>label>span]:font-medium"
                  inputClassName="text-sm"
                  onChange={formik.handleChange}
                  error={formik.errors.loginUsername}
                />
                <Password
                  id='loginPassword'
                  label="Contraseña"
                  placeholder="Introduce tu contraseña"
                  size="lg"
                  className="[&>label>span]:font-medium"
                  inputClassName="text-sm"
                  value={formik.values.loginPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.loginPassword}
                />  
                <div className="flex items-center justify-between mt-3 mb-3">
                  <Link to='/forgot-password' className="text-sm text-primary transition-colors hover:text-secondary font-bold">
                    ¿Olvidó su contraseña?
                  </Link>
                </div>
            </div>
            <Button className="w-full mt-5 disabled-primary" type="submit" size="lg" disabled={isLoading}>
              {isLoading ? <Loader className='text-white' /> : 'Iniciar Sesión'}
            </Button>   
          </form>
        </div>
      </div> 
      <div className='login-shape hidden md:flex md:col-span-2'>
        <div className='w-full h-full'></div>
      </div>
    </div>
  );
};

export default Login;